import type * as PixiNS from 'pixi.js';
import type { PixiModule } from '../animation/animationController';
import type { VisualizerLayer } from '../domain/schemas';
import type { LayerFrame, VisualizerFrame } from './evaluate';

/**
 * Draws visualizer frames into the scene renderer's two visualizer containers (Phase 4C).
 * One Pixi `Container` (transform, opacity, blend mode) holding one `Graphics` per layer,
 * created once and reused — cleared and redrawn each displayed frame, never recreated.
 * A layer's objects (and their GPU geometry) are destroyed when the layer disappears from
 * the frame, and everything in `destroy()`. Glow is a wider translucent underlay rather
 * than a blur filter, to stay cheap on modest GPUs.
 */

interface Node {
  container: PixiNS.Container;
  graphics: PixiNS.Graphics;
  zone: VisualizerLayer['placement'];
}

/** Destroys a Graphics' own context too (see NODE_DESTROY in the controller). */
const DESTROY = { children: true, context: true, texture: false, textureSource: false } as const;

const hex = (c: string) => Number.parseInt(c.slice(1), 16);

/** Colour between a and b (0–1). */
export function mix(a: number, b: number, t: number): number {
  const k = Math.min(1, Math.max(0, t));
  const ch = (s: number) => Math.round(((a >> s) & 0xff) + (((b >> s) & 0xff) - ((a >> s) & 0xff)) * k);
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

export interface VisualizerDrawStats {
  layers: number;
  /** Shapes drawn in the last frame (bars, points, rings, particles, lines). */
  elements: number;
  created: number;
  destroyed: number;
}

export class VisualizerRenderer {
  private readonly nodes = new Map<string, Node>();
  private flash: PixiNS.Graphics | null = null;
  private order = '';
  readonly stats: VisualizerDrawStats = { layers: 0, elements: 0, created: 0, destroyed: 0 };

  constructor(
    private readonly pixi: PixiModule,
    private readonly behind: PixiNS.Container,
    private readonly front: PixiNS.Container,
  ) {}

  /** Draws a frame (null hides everything and releases every layer). */
  draw(frame: VisualizerFrame | null, scene: { width: number; height: number }): void {
    const seen = new Set<string>();
    let elements = 0;
    for (const f of frame?.layers ?? []) {
      seen.add(f.layer.id);
      const node = this.node(f.layer);
      this.place(node, f.layer);
      node.graphics.clear();
      elements += drawLayer(node.graphics, f);
    }
    for (const [id, node] of this.nodes) {
      if (seen.has(id)) continue;
      node.container.destroy(DESTROY);
      this.nodes.delete(id);
      this.stats.destroyed++;
    }
    // Keep children in list order within each zone (only when the order changed).
    const order = (frame?.layers ?? []).map((f) => `${f.layer.id}:${f.layer.placement}`).join('|');
    if (order !== this.order) {
      this.order = order;
      for (const f of frame?.layers ?? []) {
        const n = this.nodes.get(f.layer.id)!;
        const parent = f.layer.placement === 'front' ? this.front : this.behind;
        parent.addChild(n.container);
      }
      if (this.flash) this.front.addChild(this.flash);
    }
    this.drawFlash(frame?.flash ?? null, scene);
    this.stats.layers = this.nodes.size;
    this.stats.elements = elements;
  }

  destroy(): void {
    for (const node of this.nodes.values()) {
      node.container.destroy(DESTROY);
      this.stats.destroyed++;
    }
    this.nodes.clear();
    this.flash?.destroy(DESTROY);
    this.flash = null;
    this.order = '';
  }

  private node(layer: VisualizerLayer): Node {
    let node = this.nodes.get(layer.id);
    if (!node) {
      const container = new this.pixi.Container();
      const graphics = new this.pixi.Graphics();
      container.addChild(graphics);
      node = { container, graphics, zone: layer.placement };
      this.nodes.set(layer.id, node);
      this.stats.created++;
      this.order = ''; // re-parent below
    }
    return node;
  }

  private place(node: Node, layer: VisualizerLayer): void {
    const c = node.container;
    c.position.set(layer.x, layer.y);
    c.scale.set(layer.scale);
    c.rotation = (layer.rotation * Math.PI) / 180;
    c.alpha = layer.opacity;
    (c as unknown as { blendMode: string }).blendMode = layer.blend;
    if (node.zone !== layer.placement) {
      node.zone = layer.placement;
      this.order = '';
    }
  }

  private drawFlash(flash: VisualizerFrame['flash'], scene: { width: number; height: number }): void {
    if (!flash) {
      this.flash?.clear();
      return;
    }
    if (!this.flash) {
      this.flash = new this.pixi.Graphics();
      this.front.addChild(this.flash);
    }
    this.flash.clear();
    this.flash.rect(0, 0, scene.width, scene.height).fill({ color: hex(flash.color), alpha: Math.min(0.6, flash.alpha) });
  }
}

/** Draws one layer frame in layer-local coordinates (origin at the box centre). Returns the element count. */
export function drawLayer(g: PixiNS.Graphics, f: LayerFrame): number {
  const l = f.layer;
  const w = l.width;
  const h = l.height;
  const c1 = hex(l.style.primary);
  const c2 = hex(l.style.secondary);
  const glow = l.style.glow;
  switch (f.type) {
    case 'bars': {
      const p = f.layer.params;
      const n = f.values.length;
      const vertical = p.orientation === 'vertical';
      const along = vertical ? h : w;
      const across = vertical ? w : h;
      const slot = along / n;
      const thick = Math.max(1, slot * (1 - p.gap));
      for (let i = 0; i < n; i++) {
        const v = Math.min(1, p.minHeight + f.values[i]! * (1 - p.minHeight));
        const len = Math.max(1, v * (p.mirrored ? across / 2 : across));
        const pos = -along / 2 + i * slot + (slot - thick) / 2;
        const color = mix(c1, c2, n > 1 ? i / (n - 1) : 0);
        // Horizontal layout: bars rise from the bottom edge (mirrored: from the centre line).
        const rect = (pad: number) => {
          const a = pos - pad;
          const t = thick + pad * 2;
          const L = len + pad;
          if (!vertical) g.rect(a, p.mirrored ? -L : across / 2 - L, t, p.mirrored ? L * 2 : L);
          else g.rect(p.mirrored ? -L : -across / 2, a, p.mirrored ? L * 2 : L, t);
        };
        if (glow > 0) {
          rect(thick * 0.6 * glow);
          g.fill({ color, alpha: 0.25 * glow });
        }
        rect(0);
        g.fill({ color, alpha: 1 });
      }
      return n;
    }
    case 'radial': {
      const p = f.layer.params;
      const n = f.values.length;
      const R = Math.min(w, h) / 2;
      const inner = p.innerRadius * R;
      const width = Math.max(1, ((2 * Math.PI * Math.max(inner, R * 0.2)) / n) * p.barWidth);
      for (let i = 0; i < n; i++) {
        const a = f.angle + (2 * Math.PI * i) / n - Math.PI / 2;
        const len = Math.max(1.5, Math.min(1.5, f.values[i]!) * p.length * R);
        const [r1, r2] = p.direction === 'outward' ? [inner, inner + len] : p.direction === 'inward' ? [inner, Math.max(0, inner - len)] : [Math.max(0, inner - len / 2), inner + len / 2];
        const cos = Math.cos(a);
        const sin = Math.sin(a);
        const color = mix(c1, c2, f.values[i]!);
        if (glow > 0) g.moveTo(cos * r1, sin * r1).lineTo(cos * r2, sin * r2).stroke({ width: width * (1 + 1.5 * glow), color, alpha: 0.25 * glow, cap: 'round' });
        g.moveTo(cos * r1, sin * r1).lineTo(cos * r2, sin * r2).stroke({ width, color, alpha: 1, cap: 'round' });
      }
      return n;
    }
    case 'waveform': {
      const p = f.layer.params;
      const n = f.points.length;
      const x = (i: number) => -w / 2 + (i / (n - 1)) * w;
      const y = (i: number, sign = 1) => -sign * f.points[i]! * (h / 2);
      const path = (sign: number) => {
        g.moveTo(x(0), y(0, sign));
        for (let i = 1; i < n; i++) g.lineTo(x(i), y(i, sign));
      };
      const sides = p.mirrored ? [1, -1] : [1];
      for (const [k, sign] of sides.entries()) {
        const color = k === 0 ? c1 : c2;
        if (p.filled) {
          g.moveTo(x(0), 0);
          for (let i = 0; i < n; i++) g.lineTo(x(i), y(i, sign));
          g.lineTo(x(n - 1), 0).fill({ color, alpha: 0.85 });
        }
        if (glow > 0) {
          path(sign);
          g.stroke({ width: p.thickness * (2 + 3 * glow), color, alpha: 0.2 * glow, join: 'round' });
        }
        path(sign);
        g.stroke({ width: p.thickness, color, alpha: 1, join: 'round', cap: 'round' });
      }
      return n * sides.length;
    }
    case 'pulse': {
      const p = f.layer.params;
      const base = Math.min(w, h);
      const shape = (size: number) => {
        const r = size / 2;
        if (p.shape === 'ring') g.circle(0, 0, r);
        else if (p.shape === 'square') g.rect(-r, -r, size, size);
        else {
          const sides = p.shape === 'triangle' ? 3 : 6;
          const pts: number[] = [];
          for (let i = 0; i < sides; i++) {
            const a = -Math.PI / 2 + (2 * Math.PI * i) / sides;
            pts.push(Math.cos(a) * r, Math.sin(a) * r);
          }
          g.poly(pts);
        }
      };
      let count = 0;
      for (const ring of f.rings) {
        const ease = 1 - (1 - ring.progress) ** 3;
        const size = Math.max(2, base * (0.1 + p.expand * 0.9 * ease));
        const alpha = Math.max(0, (1 - ring.progress) * ring.strength);
        const color = mix(c1, c2, ring.progress);
        if (glow > 0) {
          shape(size);
          g.stroke({ width: p.lineWidth * (2 + 3 * glow), color, alpha: alpha * 0.25 * glow });
        }
        shape(size);
        g.stroke({ width: p.lineWidth, color, alpha });
        count++;
      }
      if (p.core > 0) {
        shape(base * p.core * f.core);
        g.fill({ color: c1, alpha: 0.85 });
        count++;
      }
      return count;
    }
    case 'particles': {
      const d = f.particles;
      for (let i = 0; i < f.count; i++) {
        const alpha = d[i * 4 + 3]!;
        const color = mix(c2, c1, alpha);
        if (glow > 0) g.circle(d[i * 4]!, d[i * 4 + 1]!, d[i * 4 + 2]! * (1 + 2 * glow)).fill({ color, alpha: alpha * 0.25 * glow });
        g.circle(d[i * 4]!, d[i * 4 + 1]!, d[i * 4 + 2]!).fill({ color, alpha });
      }
      return f.count;
    }
    case 'grid': {
      const p = f.layer.params;
      const top = -h / 2 + p.horizon * h;
      const bottom = h / 2;
      const alpha = Math.min(1, 0.45 + 0.55 * f.glow);
      const color = mix(c1, c2, f.glow);
      const lines = (width: number, a: number) => {
        // Rows: perspective spacing (quadratic), scrolling towards the viewer.
        for (let k = 0; k < p.lines; k++) {
          const z = (k + f.offset) / p.lines;
          const y = top + (bottom - top) * z * z;
          g.moveTo(-w / 2, y).lineTo(w / 2, y);
        }
        // Columns converge to the vanishing point on the horizon.
        const cols = p.lines;
        for (let k = 0; k <= cols; k++) {
          const x = -w * 0.9 + (k / cols) * w * 1.8;
          g.moveTo(x * 0.05, top).lineTo(x, bottom);
        }
        g.stroke({ width, color, alpha: a });
      };
      if (glow > 0) lines(6 * glow + 2, alpha * 0.25 * glow);
      lines(1.5, alpha);
      g.moveTo(-w / 2, top).lineTo(w / 2, top).stroke({ width: 2, color: c2, alpha });
      return p.lines * 2 + 2;
    }
  }
}
